/**
 * Performance-optimized logging utility
 * Reduces console.log overhead and provides conditional logging
 */

interface LogConfig {
  enabled: boolean;
  level: 'debug' | 'info' | 'warn' | 'error';
  maxObjects: number;
  maxStringLength: number;
}

const defaultConfig: LogConfig = {
  enabled: process.env.NODE_ENV === 'development',
  level: 'debug',
  maxObjects: 2, // Limit object logging to prevent performance issues
  maxStringLength: 100 // Limit string length in logs
};

class PerformanceLogger {
  private config: LogConfig;
  private logCache = new Map<string, number>();
  private lastLogTime = 0;
  private readonly THROTTLE_MS = 100; // Throttle logs to max 10 per second

  constructor(config: Partial<LogConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  private shouldLog(): boolean {
    if (!this.config.enabled) return false;
    
    const now = Date.now();
    if (now - this.lastLogTime < this.THROTTLE_MS) {
      return false;
    }
    this.lastLogTime = now;
    return true;
  }

  private truncateString(str: string): string {
    if (str.length <= this.config.maxStringLength) return str;
    return str.substring(0, this.config.maxStringLength) + '...';
  }

  private safeStringify(obj: any): string {
    try {
      if (typeof obj === 'string') {
        return this.truncateString(obj);
      }
      
      if (Array.isArray(obj)) {
        const limited = obj.slice(0, this.config.maxObjects);
        return JSON.stringify(limited, null, 0);
      }
      
      if (typeof obj === 'object' && obj !== null) {
        const keys = Object.keys(obj).slice(0, this.config.maxObjects);
        const limited = keys.reduce((acc, key) => {
          acc[key] = obj[key];
          return acc;
        }, {} as any);
        return JSON.stringify(limited, null, 0);
      }
      
      return String(obj);
    } catch (error) {
      return '[Object]';
    }
  }

  debug(message: string, data?: any): void {
    if (!this.shouldLog()) return;
    if (data !== undefined) {
      console.log(`🔍 ${message}`, this.safeStringify(data));
    } else {
      console.log(`🔍 ${message}`);
    }
  }

  info(message: string, data?: any): void {
    if (!this.shouldLog()) return;
    if (data !== undefined) {
      console.log(`ℹ️ ${message}`, this.safeStringify(data));
    } else {
      console.log(`ℹ️ ${message}`);
    }
  }

  warn(message: string, data?: any): void {
    if (!this.shouldLog()) return;
    if (data !== undefined) {
      console.warn(`⚠️ ${message}`, this.safeStringify(data));
    } else {
      console.warn(`⚠️ ${message}`);
    }
  }

  error(message: string, error?: any): void {
    if (!this.config.enabled) return;
    if (error !== undefined) {
      console.error(`❌ ${message}`, error);
    } else {
      console.error(`❌ ${message}`);
    }
  }

  // Specialized logging for state changes
  stateChange(component: string, state: any): void {
    if (!this.shouldLog()) return;
    
    const stateKeys = Object.keys(state).slice(0, 3); // Only log first 3 keys
    const summary = stateKeys.reduce((acc, key) => {
      const value = state[key];
      if (Array.isArray(value)) {
        acc[key] = `[${value.length} items]`;
      } else if (typeof value === 'object' && value !== null) {
        acc[key] = `{${Object.keys(value).length} props}`;
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    
    console.log(`🔄 ${component} state:`, summary);
  }

  // Performance timing
  time(label: string): () => void {
    if (!this.config.enabled) return () => {};
    
    const start = performance.now();
    return () => {
      const end = performance.now();
      console.log(`⏱️ ${label}: ${(end - start).toFixed(2)}ms`);
    };
  }

  // Batch logging for multiple items
  batch(component: string, items: any[], maxItems: number = 3): void {
    if (!this.shouldLog() || !items || items.length === 0) return;
    
    const limited = items.slice(0, maxItems);
    const summary = {
      total: items.length,
      shown: limited.length,
      items: limited
    };
    
    console.log(`📦 ${component} batch:`, summary);
  }
}

// Create singleton instance
export const logger = new PerformanceLogger();

// Convenience functions
export const logStateChange = (component: string, state: any) => 
  logger.stateChange(component, state);

export const logPerformance = (label: string) => logger.time(label);

export const logBatch = (component: string, items: any[], maxItems?: number) => 
  logger.batch(component, items, maxItems);

export default logger;
